import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import instance from "../../service/apiService";
import { ApiConstants } from "../../config";
import {
  ApiResponse,
  AssystProvider,
  AssystDataProvider,
  BulkValuationState,
  initialState,
} from "../../types/bulkValuationTypes";

// Update initial state to include message

// Async thunks
export const fetchProviderById = createAsyncThunk<
  any,
  any,
  { rejectValue: string }
>("bulkValuation/fetchProviderById", async (id, { rejectWithValue }) => {
  try {
    const response: AxiosResponse<ApiResponse<AssystProvider[]>> =
      await instance.get(
        `${ApiConstants.systemManager.providers.providerList}/${id}`
      );
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch provider list"
    );
  }
});

export const fetchAssystProviderList = createAsyncThunk<
  AssystProvider[],
  void,
  { rejectValue: string }
>("bulkValuation/fetchAssystProviderList", async (_, { rejectWithValue }) => {
  try {
    const response: AxiosResponse<ApiResponse<AssystProvider[]>> =
      await instance.get(
        ApiConstants.systemManager.bulkValuation.assystProviderList
      );
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch provider list"
    );
  }
});

export const fetchAssystDataProvider = createAsyncThunk<
  AssystDataProvider[],
  any,
  { rejectValue: string }
>("bulkValuation/fetchAssystDataProvider", async (id, { rejectWithValue }) => {
  try {
    const response: AxiosResponse<ApiResponse<AssystDataProvider[]>> =
      await instance.get(
        `${ApiConstants.systemManager.bulkValuation.assystDataProviderList}/${id}`
      );
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch data provider"
    );
  }
});

export const updateBulkValuation = createAsyncThunk<
  {
    data: any;
    message: string;
    isError: boolean;
    error: { code: string; message: string };
  },
  { data: any },
  { rejectValue: string }
>(
  "bulkValuation/updateBulkValuation",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response: any = await instance.patch(
        `${ApiConstants.systemManager.bulkValuation.updateBulkValuation}`,
        data
      );
      return {
        data: response.data.data,
        message: response.data.message,
        isError: response.data.isError,
        error: response.data.error,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update bulk valuation"
      );
    }
  }
);

export const fetchValuationHistory = createAsyncThunk<
  any,
  {
    status?: string;
    valuationDate?: string;
    createdAt?: string;
    providerName?: string;
    dataProviderName?: string;
    page?: number;
    limit?: number;
  },
  { rejectValue: string }
>(
  "bulkValuation/fetchValuationHistory",
  async (params, { rejectWithValue }) => {
    try {
      const response = await instance.get(
        `${ApiConstants.systemManager.errorLog.list}`,
        { params }
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch valuation history"
      );
    }
  }
);

// Create the slice
const bulkValuationSlice = createSlice({
  name: "bulkValuation",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.message = null; // Clear message as well
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProviderById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(fetchProviderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedProviderData = action.payload;
      })
      .addCase(fetchProviderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch provider by ID";
      })
      .addCase(fetchAssystProviderList.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(fetchAssystProviderList.fulfilled, (state, action) => {
        state.loading = false;
        state.providers = action.payload;
      })
      .addCase(fetchAssystProviderList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch provider list";
      })
      .addCase(fetchAssystDataProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(fetchAssystDataProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.dataProvider = action.payload;
      })
      .addCase(fetchAssystDataProvider.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch data provider";
      })
      .addCase(updateBulkValuation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(updateBulkValuation.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message; // Store the success message
      })
      .addCase(updateBulkValuation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update bulk valuation";
      })
      .addCase(fetchValuationHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(fetchValuationHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.valuationHistory = action.payload?.data || [];
        state.metadata = action.payload?.metadata || {};
      })
      .addCase(fetchValuationHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch valuation history";
      });
  },
});

// Export actions and reducer
export const { clearError } = bulkValuationSlice.actions;
export default bulkValuationSlice.reducer;
